import { useParams, useLocation } from 'react-router-dom'
import { Container, Breadcrumb, Button, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import toast from 'react-hot-toast'
import ProductBundleModal from 'components/product-bundle-modal'
import ProductSelectionSection from 'components/subscription-link-builder/product-selection-section'
import {
  HubSpotDealSection,
  SettingsSection,
  SandboxSection,
  SandboxSuggestionSection,
  ManualSandboxSearchSection,
} from 'components/subscription-link-builder/configuration-section'
import SandboxModals from 'components/subscription-link-builder/sandbox-modals'
import { User } from 'interfaces/user'
import { RootState } from 'index'
import { useSubscriptionPurchaseOrderBuilder } from 'hooks/useSubscriptionPurchaseOrderBuilder'
import { LineItem } from 'interfaces/line-item'
import { LineItemManager, LineItemDisplay } from 'services/line-item-manager'
import { DiscountCalculator } from 'services/discount-calculator'
import { PRODUCT_IDS, LINQ_INFRA_PRODUCT_IDS } from 'constants/product-ids'
import { ProductName, ProductType } from 'interfaces/purchase-order'

const SubscriptionLinkBuilderPageNew = () => {
  const { dealid } = useParams<{ dealid?: string }>()
  const location = useLocation()
  const currentUser = useSelector<RootState, User | null>(
    (state) => state.user.currentUser
  )

  const queryParams = new URLSearchParams(location.search)
  const dealNameFromUrl = queryParams.get('dealName')

  // Modal state (UI management)
  const [showProductModal, setShowProductModal] = useState(false)
  const [currentProductName, setCurrentProductName] =
    useState<ProductName>('Sales Enablement')
  const [currentProductColor, setCurrentProductColor] =
    useState<string>('#2563eb')
  const [currentProductType, setCurrentProductType] =
    useState<ProductType | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [productIdToPreFetch, setProductIdToPreFetch] = useState<string>('')

  // Settings state (UI form management)
  const [hasTrial, setHasTrial] = useState(false)
  const [collectShippingAddress, setCollectShippingAddress] = useState(true)
  const [allowCustomerUseReferralCode, setAllowCustomerUseReferralCode] =
    useState(false)
  const [allowQuantityAdjusting, setAllowQuantityAdjusting] = useState(false)

  // Sandbox popup state
  const [showPhonePreferenceModal, setShowPhonePreferenceModal] =
    useState(false)
  const [showSalesEnablementConfirm, setShowSalesEnablementConfirm] =
    useState(false)
  const [showInfraNoPhoneConfirm, setShowInfraNoPhoneConfirm] = useState(false)
  const [
    showSalesEnablementNoPhoneConfirm,
    setShowSalesEnablementNoPhoneConfirm,
  ] = useState(false)
  const [showPhoneSelectionModal, setShowPhoneSelectionModal] = useState(false)
  const [pendingProductType, setPendingProductType] =
    useState<ProductType | null>(null)
  const [showQuickPhoneModal, setShowQuickPhoneModal] = useState(false)

  // Hook for business logic
  const {
    // Line items
    lineItems,
    setLineItems,
    savedLineItems,
    setSavedLineItems,
    // HubSpot
    hubspotDealLink,
    setHubspotDealLink,
    hubspotValidationResult,
    setHubspotValidationResult,
    // Checkout
    checkoutLink,
    setCheckoutLink,
    // Sandbox
    sandboxInfo,
    sandboxSuggestions,
    sandboxCustomerType,
    setSandboxCustomerType,
    sandboxPhonePreference,
    setSandboxPhonePreference,
    salesEnablementAcknowledged,
    setSalesEnablementAcknowledged,
    usePhoneFromSandboxId,
    setUsePhoneFromSandboxId,
    handleConfirmSandboxSuggestion,
    handleDismissSandboxSuggestions,
    manualSandboxEnabled,
    handleManualSandboxToggle,
    handleManualSandboxSelect,
    // CLI
    cliInfo,
    // Product bundle
    productBundle,
    bundleLoading,
    // Loading states
    stripeCheckoutLinkLoading,
    isValidatingHubspotLink,
    // Computed values
    totalAmount,
    hasOnlyLinqOne,
    // Methods
    handleGenerateLink,
    handleValidateHubspotDeal,
  } = useSubscriptionPurchaseOrderBuilder(
    currentUser?.id,
    dealid,
    dealNameFromUrl || undefined,
    productIdToPreFetch || undefined
  )

  // Settings change handler (using page state)
  const handleSettingsChange = (
    setter: (value: boolean) => void,
    value: boolean
  ) => {
    setter(value)
    setCheckoutLink(undefined)
  }

  // Product type helper functions
  const getProductIdForType = (type: ProductType): string | null => {
    switch (type) {
      case 'blue':
        return PRODUCT_IDS.LINQ_BLUE
      case 'one':
        return PRODUCT_IDS.LINQ_ONE
      case 'infra':
        return PRODUCT_IDS.LINQ_INFRA
      case 'infra200':
        return PRODUCT_IDS.LINQ_INFRA_200_CONTACT
      case 'reseller':
        return PRODUCT_IDS.LINQ_RESELLER
      default:
        return null
    }
  }

  const getProductNameForType = (type: ProductType): ProductName => {
    switch (type) {
      case 'blue':
        return 'Sales Enablement'
      case 'one':
        return 'One'
      case 'infra':
        return 'Infrastructure'
      case 'infra200':
        return 'Infras 200 Contacts'
      default:
        return 'Sales Enablement'
    }
  }

  const getProductColorForType = (type: ProductType): string => {
    switch (type) {
      case 'blue':
        return '#2563eb'
      case 'one':
        return '#10b981'
      case 'infra':
        return '#0ca5e9'
      case 'infra200':
        return '#0891b2'
      case 'reseller':
        return '#9333ea'
      default:
        return '#2563eb'
    }
  }

  // Modal handlers
  const openProductModalDirect = (type: ProductType, isEdit = false) => {
    const productName = getProductNameForType(type)
    const productColor = getProductColorForType(type)
    const productId = getProductIdForType(type)

    const productAlreadyInCart = lineItems.some(
      (item) => item.parentProductType === type
    )

    setCurrentProductName(productName)
    setCurrentProductColor(productColor)
    setCurrentProductType(type)
    setIsEditMode(isEdit || productAlreadyInCart)
    if (productId) {
      setProductIdToPreFetch(productId)
    }
    setShowProductModal(true)
  }

  const sandboxHasPhone = !!sandboxInfo?.phone_number

  const openProductModal = (type: ProductType, isEdit = false) => {
    if (!sandboxInfo) {
      openProductModalDirect(type, isEdit)
      return
    }

    // Sandbox user: intercept with popup
    if (type === 'infra' || type === 'infra200') {
      setPendingProductType(type)
      if (sandboxHasPhone) {
        // Phone still available — let user choose keep/new
        setShowPhonePreferenceModal(true)
      } else {
        // Phone already returned to pool — just confirm new number
        setShowInfraNoPhoneConfirm(true)
      }
    } else if (type === 'blue' || type === 'reseller') {
      setPendingProductType(type)
      if (sandboxHasPhone) {
        setShowSalesEnablementConfirm(true)
      } else {
        setShowSalesEnablementNoPhoneConfirm(true)
      }
    } else {
      openProductModalDirect(type, isEdit)
    }
  }

  // Sibling sandboxes that have phone numbers (for phone selection)
  const siblingSandboxesWithPhones = sandboxSuggestions.filter(
    (s) => s.phone_number && s.id !== sandboxInfo?.id
  )

  // Phone Preference popup handler
  const handlePhonePreferenceSelect = (preference: 'same' | 'different') => {
    setSandboxPhonePreference(preference)
    setSandboxCustomerType('infrastructure')
    setShowPhonePreferenceModal(false)
    setCheckoutLink(undefined)
    if (preference === 'same' && siblingSandboxesWithPhones.length > 0) {
      // Show phone selection modal to let rep pick sibling's phone
      setShowPhoneSelectionModal(true)
    } else if (pendingProductType) {
      openProductModalDirect(pendingProductType)
    }
  }

  // Phone Selection modal handler
  const handlePhoneSelection = (selectedSandboxId: number | null) => {
    setUsePhoneFromSandboxId(selectedSandboxId)
    setShowPhoneSelectionModal(false)
    setCheckoutLink(undefined)
    if (pendingProductType) {
      openProductModalDirect(pendingProductType)
    }
  }

  // Infrastructure confirm handler (phone already gone)
  const handleInfraNoPhoneConfirm = () => {
    setSandboxPhonePreference('different')
    setSandboxCustomerType('infrastructure')
    setShowInfraNoPhoneConfirm(false)
    setCheckoutLink(undefined)
    if (pendingProductType) {
      openProductModalDirect(pendingProductType)
    }
  }

  // Sales Enablement confirmation handler (with phone preference)
  const handleSalesEnablementPhonePreference = (
    preference: 'same' | 'different'
  ) => {
    setSalesEnablementAcknowledged(true)
    setSandboxCustomerType('sales_enablement')
    setSandboxPhonePreference(preference)
    setShowSalesEnablementConfirm(false)
    setCheckoutLink(undefined)
    if (preference === 'same' && siblingSandboxesWithPhones.length > 0) {
      setShowPhoneSelectionModal(true)
    } else if (pendingProductType) {
      openProductModalDirect(pendingProductType)
    }
  }

  // Sales Enablement confirmation handler (phone already gone)
  const handleSalesEnablementNoPhoneConfirm = () => {
    setSalesEnablementAcknowledged(true)
    setSandboxCustomerType('sales_enablement')
    setSandboxPhonePreference('different')
    setShowSalesEnablementConfirm(false)
    setShowSalesEnablementNoPhoneConfirm(false)
    setCheckoutLink(undefined)
    if (pendingProductType) {
      openProductModalDirect(pendingProductType)
    }
  }

  const handleEditClick = () => {
    const baseProduct = lineItems.find((item) => !item.isAddon)
    if (baseProduct?.parentProductType) {
      openProductModal(baseProduct.parentProductType, true)
    }
  }

  const handleAddProducts = (
    selectedItems: {
      priceId: string
      quantity: number
      productId: string
      discount?: number
    }[]
  ) => {
    if (!productBundle) return

    // Use LineItemManager service to build line items
    const { lineItems: newLineItems, savedLineItems: newSavedLineItems } =
      LineItemManager.buildLineItems(
        selectedItems,
        productBundle,
        savedLineItems,
        currentProductType,
        allowQuantityAdjusting
      )

    let sortedLineItems: LineItemDisplay[]
    let updatedSavedLineItems: Partial<LineItem>[]

    if (isEditMode) {
      sortedLineItems = LineItemManager.sortLineItems(newLineItems)
      updatedSavedLineItems = newSavedLineItems
      toast.success('Products updated!')
    } else {
      const allLineItems = [...lineItems, ...newLineItems]
      sortedLineItems = LineItemManager.sortLineItems(allLineItems)
      updatedSavedLineItems = [...savedLineItems, ...newSavedLineItems]
      toast.success('Products added!')
    }

    setLineItems(sortedLineItems)
    setSavedLineItems(updatedSavedLineItems)
    setShowProductModal(false)
    setIsEditMode(false)
    setCheckoutLink(undefined)
  }

  const handleClearAll = () => {
    setLineItems([])
    setSavedLineItems([])
    setCheckoutLink(undefined)
    // Reset sandbox selections when clearing cart
    setSandboxCustomerType(null)
    setSandboxPhonePreference(null)
    setSalesEnablementAcknowledged(false)
    setUsePhoneFromSandboxId(null)
  }

  const handleUpdateDiscount = (
    index: number,
    discountPercentage: number | null,
    discountPriceId?: string
  ) => {
    const item = lineItems[index]

    // If discountPriceId not provided, try to find it from product bundle
    let finalDiscountPriceId: string | undefined = discountPriceId
    if (discountPercentage && !discountPriceId && productBundle) {
      finalDiscountPriceId = DiscountCalculator.getDiscountPriceId(
        productBundle,
        item.period,
        discountPercentage,
        item.productId
      )
    }

    const updatedLineItems = lineItems.map((lineItem, i) =>
      i === index
        ? {
            ...lineItem,
            discount: discountPercentage ?? 0,
            discountedPriceId: discountPercentage
              ? finalDiscountPriceId
              : undefined,
            isManualDiscount: true,
          }
        : lineItem
    )
    setLineItems(updatedLineItems)

    const updatedSavedLineItems = savedLineItems.map((savedItem, i) =>
      i === index
        ? {
            ...savedItem,
            discount: discountPercentage || undefined,
            price: finalDiscountPriceId || item.priceId,
          }
        : savedItem
    )
    setSavedLineItems(updatedSavedLineItems)
    setCheckoutLink(undefined)
  }

  // The backend labels customers by this name — both infra products must be
  // reported as "Infrastructure" (see BUG-8233). Derived from the cart so a
  // page refresh can't reset it to the default modal state.
  const checkoutProductName: 'Sales Enablement' | 'One' | 'Infrastructure' =
    lineItems.some((item) => LINQ_INFRA_PRODUCT_IDS.includes(item.productId))
      ? 'Infrastructure'
      : currentProductName === 'Infras 200 Contacts'
      ? 'Infrastructure'
      : currentProductName

  const getCustomerTypeFromCart = (): 'infrastructure' | 'sales_enablement' => {
    const hasBlue = lineItems.some((i) => i.productId === PRODUCT_IDS.LINQ_BLUE)
    return hasBlue ? 'sales_enablement' : 'infrastructure'
  }

  const onClickGenerate = () => {
    const settings = {
      hasTrial,
      collectShippingAddress,
      allowCustomerUseReferralCode,
    }

    // No sandbox — just generate
    if (!sandboxInfo) {
      handleGenerateLink(settings, checkoutProductName)
      return
    }

    // Phone preference already selected (from product-add flow) — just generate
    if (sandboxPhonePreference) {
      handleGenerateLink(settings, checkoutProductName)
      return
    }

    // Sandbox has phone — ask user
    if (sandboxHasPhone) {
      setShowQuickPhoneModal(true)
      return
    }

    // Sandbox has no phone — auto "different" and generate
    const customerType = sandboxCustomerType || getCustomerTypeFromCart()
    handleGenerateLink(settings, checkoutProductName, {
      phonePreference: 'different',
      customerType,
    })
  }

  const handleQuickPhoneSelect = (preference: 'same' | 'different') => {
    const customerType = sandboxCustomerType || getCustomerTypeFromCart()
    setSandboxPhonePreference(preference)
    setSandboxCustomerType(customerType)
    if (customerType === 'sales_enablement')
      setSalesEnablementAcknowledged(true)
    setShowQuickPhoneModal(false)
    handleGenerateLink(
      { hasTrial, collectShippingAddress, allowCustomerUseReferralCode },
      checkoutProductName,
      { phonePreference: preference, customerType }
    )
  }

  return (
    <Container style={{ marginTop: '100px' }}>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active>New Purchase Order</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0 }}>
          New Purchase Order
        </h1>
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="primary"
            onClick={onClickGenerate}
            disabled={
              (lineItems.length === 0 ||
                !hubspotValidationResult?.isValid ||
                stripeCheckoutLinkLoading) &&
              !checkoutLink
            }
          >
            {stripeCheckoutLinkLoading
              ? 'Generating...'
              : checkoutLink
              ? 'Copy'
              : 'Create'}
          </Button>
        </div>
      </div>

      <HubSpotDealSection
        hubspotDealLink={hubspotDealLink}
        onHubspotDealLinkChange={(value) => {
          setHubspotDealLink(value)
          setHubspotValidationResult(null)
        }}
        dealid={dealid}
        hubspotValidationResult={hubspotValidationResult}
        onValidateDeal={handleValidateHubspotDeal}
        isValidatingHubspotLink={isValidatingHubspotLink}
        currentUserName={currentUser?.name || currentUser?.email}
        dealName={dealNameFromUrl || undefined}
      />

      {cliInfo && (
        <div
          className="text-center fw-bold mt-3"
          style={{
            backgroundColor: '#2E5984',
            color: '#fff',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '0.85rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          CLI User — Converting From Shared Line
        </div>
      )}

      {!cliInfo && !sandboxInfo && sandboxSuggestions.length > 0 && (
        <SandboxSuggestionSection
          suggestions={sandboxSuggestions}
          onConfirm={handleConfirmSandboxSuggestion}
          onDismiss={handleDismissSandboxSuggestions}
        />
      )}

      {!cliInfo && sandboxInfo && <SandboxSection sandboxInfo={sandboxInfo} />}

      {hubspotValidationResult?.isValid &&
        !cliInfo &&
        !sandboxInfo &&
        sandboxSuggestions.length === 0 && (
          <ManualSandboxSearchSection
            isEnabled={manualSandboxEnabled}
            onToggle={handleManualSandboxToggle}
            onSelect={handleManualSandboxSelect}
          />
        )}

      <ProductSelectionSection
        lineItems={lineItems}
        totalAmount={totalAmount}
        onProductClick={openProductModal}
        onClearAll={handleClearAll}
        onUpdateDiscount={handleUpdateDiscount}
        onEditClick={handleEditClick}
        prices={productBundle?.prices}
        isLoadingPrices={bundleLoading}
        sandboxInfo={sandboxInfo}
        sandboxPhonePreference={sandboxPhonePreference}
        onSandboxPhonePreferenceChange={(value) => {
          setSandboxPhonePreference(value)
          setUsePhoneFromSandboxId(null)
          setCheckoutLink(undefined)
        }}
        usePhoneFromSandboxId={usePhoneFromSandboxId}
        sandboxSuggestions={sandboxSuggestions}
      />

      <SettingsSection
        hasLinqOneInCart={hasOnlyLinqOne}
        hasTrial={hasTrial}
        onTrialChange={(value) => handleSettingsChange(setHasTrial, value)}
        allowQuantityAdjusting={allowQuantityAdjusting}
        onAllowQuantityAdjustingChange={(value) =>
          handleSettingsChange(setAllowQuantityAdjusting, value)
        }
        allowCustomerUseReferralCode={allowCustomerUseReferralCode}
        onAllowCustomerUseReferralCodeChange={(value) =>
          handleSettingsChange(setAllowCustomerUseReferralCode, value)
        }
        collectShippingAddress={collectShippingAddress}
        onCollectShippingAddressChange={(value) =>
          handleSettingsChange(setCollectShippingAddress, value)
        }
      />

      {/* Generate Button */}
      <Button
        variant="primary"
        onClick={onClickGenerate}
        disabled={
          (lineItems.length === 0 ||
            !hubspotValidationResult?.isValid ||
            stripeCheckoutLinkLoading) &&
          !checkoutLink
        }
        className="w-100 mb-4"
        size="lg"
      >
        {stripeCheckoutLinkLoading
          ? 'Generating Link...'
          : checkoutLink
          ? 'Copy Link'
          : 'Create Subscription Link'}
      </Button>

      {/* Product Bundle Modal */}
      <ProductBundleModal
        show={showProductModal}
        onHide={() => {
          setShowProductModal(false)
          setIsEditMode(false)
        }}
        productName={currentProductName}
        productColor={currentProductColor}
        baseProduct={productBundle?.base_product || null}
        addonProducts={productBundle?.addon_products || []}
        additionalProducts={productBundle?.additional_products || []}
        prices={productBundle?.prices || {}}
        isLoading={bundleLoading}
        isEditMode={isEditMode}
        existingItems={
          isEditMode
            ? lineItems.map((item) => ({
                priceId: item.priceId,
                quantity: item.quantity,
                productId: item.productId,
              }))
            : []
        }
        onAddProducts={handleAddProducts}
      />

      <SandboxModals
        showPhonePreferenceModal={showPhonePreferenceModal}
        onHidePhonePreferenceModal={() => {
          setShowPhonePreferenceModal(false)
          setPendingProductType(null)
        }}
        onPhonePreferenceSelect={handlePhonePreferenceSelect}
        showInfraNoPhoneConfirm={showInfraNoPhoneConfirm}
        onHideInfraNoPhoneConfirm={() => {
          setShowInfraNoPhoneConfirm(false)
          setPendingProductType(null)
        }}
        onInfraNoPhoneConfirm={handleInfraNoPhoneConfirm}
        sandboxStatus={sandboxInfo?.status}
        showSalesEnablementConfirm={showSalesEnablementConfirm}
        onHideSalesEnablementConfirm={() => {
          setShowSalesEnablementConfirm(false)
          setPendingProductType(null)
        }}
        onSalesEnablementPhonePreference={handleSalesEnablementPhonePreference}
        showSalesEnablementNoPhoneConfirm={showSalesEnablementNoPhoneConfirm}
        onHideSalesEnablementNoPhoneConfirm={() => {
          setShowSalesEnablementNoPhoneConfirm(false)
          setPendingProductType(null)
        }}
        onSalesEnablementNoPhoneConfirm={handleSalesEnablementNoPhoneConfirm}
        showPhoneSelectionModal={showPhoneSelectionModal}
        onHidePhoneSelectionModal={() => {
          setShowPhoneSelectionModal(false)
          setUsePhoneFromSandboxId(null)
          setPendingProductType(null)
        }}
        onPhoneSelection={handlePhoneSelection}
        sandboxInfo={sandboxInfo}
        usePhoneFromSandboxId={usePhoneFromSandboxId}
        setUsePhoneFromSandboxId={setUsePhoneFromSandboxId}
        siblingSandboxesWithPhones={siblingSandboxesWithPhones}
      />

      {/* Quick phone preference modal — shown when generating link for sandbox */}
      <Modal
        show={showQuickPhoneModal}
        onHide={() => setShowQuickPhoneModal(false)}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Phone Number Preference
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#a0aec0', marginBottom: '1.5rem' }}>
            This customer has an existing sandbox phone number. Would you like
            to keep it or assign a new one?
          </p>
          <div className="d-flex flex-column gap-3">
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => handleQuickPhoneSelect('same')}
            >
              <div className="fw-bold">Keep existing number</div>
              <small className="text-muted">{sandboxInfo?.phone_number}</small>
            </Button>
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => handleQuickPhoneSelect('different')}
            >
              <div className="fw-bold">Assign a new number</div>
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  )
}

export default SubscriptionLinkBuilderPageNew
